import { api } from "@/lib/api";

export class AdminReauthRequiredError extends Error {
  constructor() {
    super("admin_reauth_required");
    this.name = "AdminReauthRequiredError";
  }
}

// Module-level reauth callback: set by the admin layout's useAdminReauth hook.
// When an admin request gets 401 admin_reauth_required, adminRequest awaits this
// callback, which opens the modal and resolves when the user completes reauth.
type ReauthCallback = () => Promise<void>;
let globalReauthCallback: ReauthCallback | null = null;

export function setAdminReauthCallback(cb: ReauthCallback): void {
  globalReauthCallback = cb;
}

export function clearAdminReauthCallback(): void {
  globalReauthCallback = null;
}

async function adminRequest<T>(
  fn: () => Promise<{ ok: boolean; status: number; body: T | null }>,
): Promise<T> {
  const result = await fn();
  if (
    result.status === 401 &&
    (result.body as { error?: string } | null)?.error === "admin_reauth_required"
  ) {
    if (!globalReauthCallback) throw new AdminReauthRequiredError();
    await globalReauthCallback();
    const retry = await fn();
    if (!retry.ok) throw new Error(`admin request failed after reauth: ${retry.status}`);
    return retry.body as T;
  }
  if (!result.ok) throw new Error(`admin request failed: ${result.status}`);
  return result.body as T;
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface FlagWithContext {
  id: number;
  reason: string;
  details: string;
  created_at: string;
  source: string;
  artifact_id: string;
  artifact_title: string;
  artifact_body: string;
  taken_down_at: string | null;
  automaton_id: string;
  automaton_handle: string;
  automaton_status: string;
  reporter_id: string | null;
  reporter_email: string | null;
}

export interface UserSummary {
  id: string;
  email: string;
  role: string;
  created_at: string;
  deleted_at: string | null;
  balance_c: number;
  automaton_count: number;
}

export interface UserSession {
  created_at: string;
  expires_at: string;
  ip: string;
  user_agent: string;
}

export interface AutomatonSummary {
  id: string;
  handle: string;
  status: string;
  created_at: string;
}

export interface LedgerEntry {
  created_at: string;
  amount_c: number;
  reason: string;
}

export interface UserDetail {
  user: UserSummary;
  sessions: UserSession[];
  automatons: AutomatonSummary[];
  ledger: LedgerEntry[];
}

export interface DashboardCounts {
  open_flags: number;
  banned_24h: number;
  live_automatons: number;
  revenue_today_c: number;
}

export interface PricingRow {
  key: string;
  value_int: number;
  updated_at: string;
  updated_by: string | null;
}

// ---------------------------------------------------------------------------
// API methods
// ---------------------------------------------------------------------------

export const adminApi = {
  reauth(totpCode: string) {
    return api.post<void>("/v1/admin/reauth", { totp_code: totpCode });
  },

  healthCheck(): Promise<void> {
    return adminRequest(() => api.get<void>("/v1/admin/_health"));
  },

  // Phase C: Flags
  listFlags(): Promise<{ flags: FlagWithContext[] }> {
    return adminRequest(() => api.get<{ flags: FlagWithContext[] }>("/v1/admin/flags"));
  },

  resolveFlag(id: number, action: "approve" | "takedown"): Promise<void> {
    return adminRequest(() => api.patch<void>(`/v1/admin/flags/${id}`, { action }));
  },

  // Phase D1: Users (read)
  listUsers(
    q: string,
    limit: number,
    offset: number,
  ): Promise<{ users: UserSummary[]; total: number; limit: number; offset: number }> {
    const params = new URLSearchParams({ q, limit: String(limit), offset: String(offset) });
    return adminRequest(() =>
      api.get<{ users: UserSummary[]; total: number; limit: number; offset: number }>(
        `/v1/admin/users?${params.toString()}`,
      ),
    );
  },

  getUser(id: string): Promise<UserDetail> {
    return adminRequest(() => api.get<UserDetail>(`/v1/admin/users/${id}`));
  },

  getDashboard(): Promise<DashboardCounts> {
    return adminRequest(() => api.get<DashboardCounts>("/v1/admin/dashboard"));
  },

  // Phase D2: User mutations
  setUserRole(id: string, role: "user" | "admin"): Promise<void> {
    return adminRequest(() => api.patch<void>(`/v1/admin/users/${id}/role`, { role }));
  },

  adjustWallet(
    id: string,
    params: { amount_c: number; action: "credit" | "debit"; reason: string },
  ): Promise<void> {
    return adminRequest(() => api.post<void>(`/v1/admin/users/${id}/wallet`, params));
  },

  banUser(id: string, reason: string): Promise<void> {
    return adminRequest(() => api.post<void>(`/v1/admin/users/${id}/ban`, { reason }));
  },

  banAutomaton(id: string, reason: string): Promise<void> {
    return adminRequest(() => api.post<void>(`/v1/admin/automatons/${id}/ban`, { reason }));
  },

  // Phase E: Pricing
  listPricing(): Promise<{ rows: PricingRow[] }> {
    return adminRequest(() => api.get<{ rows: PricingRow[] }>("/v1/admin/pricing"));
  },

  updatePricing(key: string, valueInt: number): Promise<void> {
    return adminRequest(() => api.patch<void>(`/v1/admin/pricing/${key}`, { value_int: valueInt }));
  },
};
